<template>
  <lb-dialog 
    v-model="dialogVisible" 
    heading="Status Insights" 
    width="70%"
    data-testid="status-insights-dialog"
  >
    <template v-slot:body>
      <!-- RU Approval Status Section -->
      <div class="status-insights-dialog__approval-status mb-4 d-flex align-center" data-testid="status-insights-dialog__approval-status">
        <span class="status-label mr-3">Status</span>
        <div class="status-indicator-wrapper" data-testid="status-insights-dialog__approval-indicator">
          <StatusIndicator
            :variant="approvalStatusVariant"
            :customLabel="approvalStatusText"
            size="sm"
            outlined
            cornerRadius="8px"
          />
        </div>
      </div>

      <v-divider class="mb-4"></v-divider>

      <!-- Tabs Section -->
      <v-tabs 
        v-model="activeTab" 
        background-color="transparent"
        class="status-insights-tabs"
        data-testid="status-insights-dialog__tabs"
      >
        <v-tab data-testid="status-insights-dialog__tab-commentary-overview">
          Commentary Overview Status
        </v-tab>
        <v-tab data-testid="status-insights-dialog__tab-workflow-status">
          Workflow Status
        </v-tab>
        <v-tab data-testid="status-insights-dialog__tab-others">
          Others
        </v-tab>
      </v-tabs>
      <v-divider/>
      <v-tabs-items v-model="activeTab" class="mt-4">
        <!-- Commentary Overview Status Tab -->
        <v-tab-item data-testid="status-insights-dialog__tab-content-commentary-overview">
          <RUStatusSummaryTable
            :localLabel="commentary_local_key_label"
            :regionalLabel="commentary_regional_key_label"
            :comparisons="['proforma', 'forecast']"
            :commentarysummarydata="mergedSummaryData"
            :loading="isLoading"
            data-testid="status-insights-dialog__status-summary-table"
          />
        </v-tab-item>

        <!-- Workflow Status Tab -->
        <v-tab-item data-testid="status-insights-dialog__tab-content-workflow-status">
          <WorkflowStatusTable
            :allworkflowstext="allworkflowstext"
            :workflowtext="workflowtext"
            :allworkflowskeys="allworkflowskeys"
            data-testid="status-insights-dialog__workflow-status-table"
          />
        </v-tab-item>

        <!-- Others Tab -->
        <v-tab-item data-testid="status-insights-dialog__tab-content-others">
          <div class="workflow-status-table">
            <v-simple-table class="workflow-table">
              <thead class="tableHeaderBg">
                <tr>
                  <th class="text-left">Features</th>
                  <th class="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="workflow-type-cell">SOD (Segregation of Duties)</td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <StatusIndicator
                        :variant="isSodEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :custom-label="isSodEnabled ? 'Enabled' : 'Disabled'"
                        size="sm"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="workflow-type-cell">Bulk Approve</td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <StatusIndicator
                        :variant="isBulkApproveEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :custom-label="isBulkApproveEnabled ? 'Enabled' : 'Disabled'"
                        size="sm"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="workflow-type-cell">Regional Level Auto Submission</td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <StatusIndicator
                        :variant="isRegionalAutoSubmissionEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :custom-label="isRegionalAutoSubmissionEnabled ? 'Enabled' : 'Disabled'"
                        size="sm"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </lb-dialog>
</template>

<script>
/**
 * StatusInsightsDialog Component
 * Displays comprehensive status information including:
 * - RU approval status
 * - Commentary overview with summary table
 * - Workflow status
 */
import RUStatusSummaryTable from '../components/RUStatusSummaryTable.vue';
import WorkflowStatusTable from '../components/WorkflowStatusTable.vue';
import StatusIndicator from '../components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '../utils/statusConstants';

export default {
  name: 'StatusInsightsDialog',
  components: {
    RUStatusSummaryTable,
    WorkflowStatusTable,
    StatusIndicator,
  },
  props: {
    /** @type {Boolean} v-model binding for dialog visibility */
    value: { type: Boolean, default: false },
    
    /** @type {Object} Current commentary cycle data */
    commentarycycledata: { type: Object, default: () => ({}) },
    
    /** @type {Object} Commentary summary data from API */
    commentarysummarydata: { type: Object, default: () => ({}) },
    
    /** @type {Object} Workflow configuration */
    workflows: { 
      type: Object, 
      default: () => ({ 
        islocalenabled: true, 
        isregionalenabled: true,
        isworkflowsenabled: true 
      }) 
    },
    
    /** @type {String} Label for local commentary */
    commentary_local_key_label: { type: String, default: 'Local' },
    
    /** @type {String} Label for regional commentary */
    commentary_regional_key_label: { type: String, default: 'Regional' },
    
    /** @type {String} Label for Reporting Unit (RU) */
    ruLabel: { type: String, default: 'RU' },
    
    /** @type {Array} All workflow text labels */
    allworkflowstext: { type: Array, default: () => [] },
    
    /** @type {Array} Current disabled workflow text */
    workflowtext: { type: Array, default: () => [] },
    
    /** @type {Array} All workflow keys for matching */
    allworkflowskeys: { type: Array, default: () => [] },
    
    /** @type {Boolean} Whether SOD (Segregation of Duties) is enabled */
    isSodEnabled: { type: Boolean, default: true },

    /** @type {Boolean} Whether Bulk Approve is enabled */
    isBulkApproveEnabled: { type: Boolean, default: false },

    /** @type {Boolean} Whether Regional Auto Submission is enabled */
    isRegionalAutoSubmissionEnabled: { type: Boolean, default: false },
  },
  data() {
    return {
      /** @type {Number} Active tab index */
      activeTab: 0,
      STATUS_VARIANTS,
      localSummaryData: {},
      isLoading: false,
    };
  },
  computed: {
    mergedSummaryData() {
        return { ...this.commentarysummarydata, ...this.localSummaryData };
    },
    /**
     * Computed property for two-way binding of dialog visibility
     */
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    
    /**
     * Determine approval status text based on workflow state
     */
    approvalStatusText() {
      const localApproved = this.commentarycycledata.local_ru_approve && this.workflows.islocalenabled;
      const regionalApproved = this.commentarycycledata.regional_ru_approve && this.workflows.isregionalenabled;
      
      if (localApproved && regionalApproved) {
        return `${this.ruLabel} level approval completed at ${this.commentary_local_key_label} & ${this.commentary_regional_key_label} level`;
      }
      if (localApproved) {
        return `${this.ruLabel} level approval completed at ${this.commentary_local_key_label} level`;
      }
      if (regionalApproved) {
        return `${this.ruLabel} level approval completed at ${this.commentary_regional_key_label} level`;
      }
      if (this.workflows.isworkflowsenabled) {
        return `${this.ruLabel} level approval pending`;
      }
      return 'No workflows enabled';
    },
    
    /**
     * Determine if approval is complete (green) or pending (blue)
     */
    isApprovalComplete() {
      const localApproved = this.commentarycycledata.local_ru_approve && this.workflows.islocalenabled;
      const regionalApproved = this.commentarycycledata.regional_ru_approve && this.workflows.isregionalenabled;
      return localApproved || regionalApproved;
    },
    
    /**
     * Resolve the StatusIndicator variant so chip colors stay in sync with shared status styles.
     */
    approvalStatusVariant() {
      if (!this.workflows.isworkflowsenabled) {
        return STATUS_VARIANTS.DISABLED;
      }
      if (this.isApprovalComplete) {
        return STATUS_VARIANTS.APPROVED;
      }
      const localApproved = this.commentarycycledata.local_ru_approve && this.workflows.islocalenabled;
      const regionalApproved = this.commentarycycledata.regional_ru_approve && this.workflows.isregionalenabled;
      if (localApproved || regionalApproved) {
        return STATUS_VARIANTS.AWAITING;
      }
      return STATUS_VARIANTS.LOCAL_AWAITED;
    },
  },
  mounted() {
    // Fetch data when component is mounted (critical for v-if usage)
    if (this.value) {
      this.fetchSummaryData();
      this.activeTab = 0;
    }
  },
  watch: {
    /**
     * Reset active tab when dialog opens
     */
    value(newVal) {
      if (newVal) {
        this.activeTab = 0;
        this.fetchSummaryData();
      } else {
        this.localSummaryData = {};
      }
    },
  },
  methods: {
    async fetchSummaryData() {
      // console.log('[StatusInsightsDialog] fetchSummaryData called', this.commentarycycledata);
      
      if (!this.commentarycycledata || !this.commentarycycledata._id) {
         // console.warn('[StatusInsightsDialog] Missing commentarycycledata or _id');
         return;
      }

      this.isLoading = true;

      const cycleId = this.commentarycycledata._id;
      // Handle reportingunit (if passed as object with _id or direct id)
      // Safely access _id if it's an object, otherwise use the value directly
      const reportingunit = (this.commentarycycledata.reportingunit && typeof this.commentarycycledata.reportingunit === 'object')
        ? this.commentarycycledata.reportingunit._id 
        : this.commentarycycledata.reportingunit;
        
      const period = this.commentarycycledata.period;
      // Usually type is 'Actual' or 'Forecast'
      const cycletype = this.commentarycycledata.type; 

      try {
        const result = await this.$store.dispatch('varianceanalysis/reportingUnit/getCycleSummaries', {
           cycleIds: [cycleId],
           reportingunit,
           period,
           cycletype,
           axiosInstance: this.axios
        });
        
        if (result && result.length > 0) {
           this.localSummaryData = result[0];
        } else {
           console.warn('[StatusInsightsDialog] No summary data returned');
        }
      } catch (e) {
        console.error("[StatusInsightsDialog] Failed to fetch summary data", e);
      } finally {
        this.isLoading = false;
      }
    }
  },
};
</script>

<style scoped>
.status-insights-dialog__approval-status {
  padding: 8px 0;
}

.status-label {
  color: var(--Text-Grey-Default, #2C2C35);
   
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem;
  text-transform: capitalize;
}

/* Wrapper lets shared StatusIndicator scale naturally within layout */
.status-indicator-wrapper {
  min-width: 0;
}

/* Tabs styling */
.status-insights-tabs {
  justify-content: flex-start;
}

.status-insights-tabs ::v-deep .v-tabs-bar {
  background-color: transparent !important;
}

.status-insights-tabs ::v-deep .v-tab {
  color: var(--text-gray-sub, #828285);
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  text-transform: capitalize;
  letter-spacing: normal;
  justify-content: flex-start;
  min-width: auto;
}

.status-insights-tabs ::v-deep .v-tab--active {
  color: var(--Primary-Colour, #193541);
  font-weight: 600;
}

.status-insights-tabs ::v-deep .v-tabs-slider {
  background-color: var(--Primary-Colour, #193541) !important;
}

.gap-4 {
  gap: 16px;
}


.workflow-table {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
}


.workflow-table td {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.workflow-type-cell {
  font-weight: 500;
}

.workflow-table tbody tr:nth-child(odd) {
  background-color: #fafafa;
}

.workflow-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}

.workflow-table tbody tr:hover {
  background-color: #f0f0f0 !important;
}
</style>
